import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import ProductImageCarousel from "@/components/ProductImageCarousel";

export default function ProductCard({ product, viewCategory }) {
  const { user } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const priceBySize = product.price_by_size && Object.keys(product.price_by_size).length ? product.price_by_size : null;
  const minVariantPrice = priceBySize ? Math.min(...Object.values(priceBySize)) : null;
  const displayPrice = priceBySize ? minVariantPrice : (product.is_special && product.special_price ? product.special_price : product.price);
  const hasDiscount = !priceBySize && product.is_special && product.special_price;
  const wishlisted = isInWishlist(product.id);
  // Pick the correct primary image based on where the card is being viewed.
  const primaryImage =
    (viewCategory === "hockey" && product.hockey_image_url) ||
    (viewCategory === "netball" && product.netball_image_url) ||
    product.image_url;
  // Single source of truth for the photo count = the product's own images[] (deduped).
  // A category-specific hero (hockey/netball) is used only as the card's primary display
  // and must NOT inflate the photo count.
  const productImages = Array.from(new Set((Array.isArray(product.images) ? product.images : []).filter(Boolean)));
  const imageList = productImages.length > 0
    ? productImages
    : (primaryImage ? [primaryImage] : []);

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    toggleWishlist(product.id);
  };

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}
      className="product-card block bg-[#171717] border border-[#262626] overflow-hidden group">
      <div className="relative aspect-[3/4] overflow-hidden bg-[#0A0A0A]">
        <ProductImageCarousel
          images={imageList}
          alt={product.name}
          className="absolute inset-0 w-full h-full"
          testId={`product-carousel-${product.id}`}
        />
        {hasDiscount && (
          <div className="absolute top-3 left-3 bg-[#F7931E] text-black text-xs font-bold uppercase tracking-wider px-3 py-1">Sale</div>
        )}
        {product.stock > 0 && product.stock <= 5 && (
          <div className="absolute top-3 right-12 bg-red-600 text-white text-xs font-bold px-3 py-1 animate-pulse">Less than 5 left!</div>
        )}
        {product.stock > 5 && product.stock <= 10 && (
          <div className="absolute top-3 right-12 bg-yellow-600 text-white text-xs font-bold px-3 py-1">Low Stock</div>
        )}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-bold uppercase tracking-wider text-lg">Sold Out</span>
          </div>
        )}
        {user && (
          <button onClick={handleWishlist} data-testid={`wishlist-toggle-${product.id}`}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/80 transition-colors z-10">
            <Heart size={16} className={wishlisted ? "fill-[#F7931E] text-[#F7931E]" : "text-white"} />
          </button>
        )}
      </div>
      <div className="p-4">
        <p className="text-[#F7931E] text-xs font-bold uppercase tracking-[0.2em] mb-1">{product.subcategory}</p>
        <h3 className="text-white font-bold text-sm sm:text-base font-heading uppercase tracking-tight leading-tight mb-2">{product.name}</h3>
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-lg">{priceBySize ? `From R${minVariantPrice.toFixed(2)}` : `R${displayPrice.toFixed(2)}`}</span>
          {hasDiscount && <span className="text-[#525252] line-through text-sm">R{product.price.toFixed(2)}</span>}
        </div>
        {product.avg_rating > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Star size={12} className="fill-[#F7931E] text-[#F7931E]" />
            <span className="text-[#A3A3A3] text-xs">{product.avg_rating} ({product.review_count})</span>
          </div>
        )}
      </div>
    </Link>
  );
}
